import type { NatsConnection } from "nats.ws";

import type { SubjectScheme } from "@/natsstubs/wire/context";
import { NatsApiCaller } from "@/natsstubs/wire/nats/NatsApiCaller";

import type { AlmTool, AlmToolCallOpts, RpcBackend, UnknownRecord } from "./RpcBackend";

type Adapter = (
  args: unknown[],
  kwargs?: UnknownRecord,
  sessionId?: string,
  timeout?: number,
) => Promise<unknown>;

/**
 * Groups methods register under. The rollout groups (`core`, `alm-common`,
 * `alm-tools`) are gated by their `nats-piadmin-<group>` release flag. The
 * `nats-only` group is the exception: those methods have no WAMP counterpart,
 * so they always route to NATS regardless of any flag.
 */
export type NatsGroup = "core" | "alm-common" | "alm-tools" | "nats-only";

export class NatsRpcBackend implements RpcBackend {
  readonly caller: NatsApiCaller;

  private readonly adapters: Record<string, Record<string, Adapter>> = {
    root: {},
    session: {},
    alm: {},
    "alm.jira": {},
    "alm.ado": {},
    "alm.ac": {},
    "almsync.jira": {},
    "almsync.ado": {},
    "almsync.ac": {},
    onboarding: {},
  };

  // submodule -> (method -> release-flag group), e.g. session.get_teams -> "core".
  private readonly methodGroups: Record<string, Record<string, NatsGroup>> = {};

  constructor(
    connection: NatsConnection,
    companyId: string,
    userId: string,
    role: string,
    scheme: SubjectScheme,
  ) {
    this.caller = new NatsApiCaller(
      { connection, userId, clientId: crypto.randomUUID(), defaultTimeout: 15000, scheme },
      { companyId, userId, role },
      (ctx) => ({ ...ctx, correlationId: crypto.randomUUID() }),
    );
  }

  register(submodule: string, group: NatsGroup, adapters: Record<string, Adapter>): void {
    this.adapters[submodule] = { ...this.adapters[submodule], ...adapters };
    const groups = this.methodGroups[submodule] ?? {};
    for (const method of Object.keys(adapters)) groups[method] = group;
    this.methodGroups[submodule] = groups;
  }

  canHandle(submodule: string, method: string): boolean {
    return Boolean(this.adapters[submodule]?.[method]);
  }

  /** Release-flag group of a registered method (`nats-piadmin-<group>`), or null if unregistered. */
  groupFor(submodule: string, method: string): NatsGroup | null {
    return this.methodGroups[submodule]?.[method] ?? null;
  }

  private dispatch<T>(
    submodule: string,
    method: string,
    args: unknown[] = [],
    kwargs?: UnknownRecord,
    sessionId?: string,
    timeout?: number,
  ): Promise<T> {
    const adapter = this.adapters[submodule]?.[method];
    if (!adapter) {
      return Promise.reject(
        new Error(
          `NATS transport has no adapter for ${submodule}.${method}; this call must stay on WAMP.`,
        ),
      );
    }
    return adapter(args, kwargs, sessionId, timeout) as Promise<T>;
  }

  rootCall<T>(method: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.dispatch("root", method, args, kwargs);
  }

  sessionCall<T>(
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    sessionId?: string,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    return this.dispatch("session", method, args, kwargs, sessionId, opts?.timeout);
  }

  almCall<T>(method: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.dispatch("alm", method, args, kwargs);
  }

  almToolCall<T>(
    tool: AlmTool,
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    return this.dispatch(`alm.${tool}`, method, args, kwargs, undefined, opts?.timeout);
  }

  almSyncCall<T>(
    tool: AlmTool,
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    sessionId?: string,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    return this.dispatch(`almsync.${tool}`, method, args, kwargs, sessionId, opts?.timeout);
  }

  onboardingCall<T>(
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    return this.dispatch("onboarding", method, args, kwargs, undefined, opts?.timeout);
  }
}
